/**
 * ContactPage component for collecting user information before assessment
 */
import React from 'react';
import { useAssessmentContext } from '../context/AssessmentContext';
import * as apiService from '../services/apiService';
import { User } from '../models/User';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Timer, Layers, LineChart } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAssessmentContext();
  
  const handleComplete = async (user: User) => {
    if (!user || !user.id) {
      console.error('User not available to create assessment');
      // Optionally, display an error to the user
      return;
    }
    try {
      const newAssessment = await apiService.createAssessment(user.id);
      dispatch({
        type: 'SET_ASSESSMENT',
        payload: newAssessment
      });
      // Navigate to the first question of the new assessment
      navigate(`/assessment/${newAssessment.id}/question/0`);
    } catch (error) {
      console.error('Failed to create assessment:', error);
      // Optionally, display an error to the user
    }
  };
  
  return (
    <div className="container mx-auto px-4 pb-16 pt-32 md:pt-36">
      <motion.div 
        className="max-w-7xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-10">
          <motion.h1 
            className="text-3xl md:text-4xl font-bold text-charcoal mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Before We Begin Your Assessment
          </motion.h1>
          <motion.p 
            className="text-lg text-charcoal-light max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Please provide your contact information so we can deliver your personalized PMO maturity results 
            and tailored recommendations.
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Contact Form - Takes 2/3 of the width on large screens */}
          <motion.div 
            className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="p-6 md:p-8">
              <ContactForm onComplete={handleComplete} />
            </div>
          </motion.div>
          
          {/* Assessment Info - Takes 1/3 of the width on large screens */}
          <motion.div 
            className="bg-white rounded-xl shadow-md overflow-hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-charcoal mb-4">About This Assessment</h2>
              
              <div className="space-y-2">
                <div className="flex items-start mb-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center mr-3 mt-1 relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gold to-amber-300"></div>
                    <div className="absolute inset-x-0 top-0 h-[1px] bg-white/30 rounded-t-full"></div>
                    <Timer className="h-5 w-5 text-black relative z-10" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal">Quick to Complete</h3>
                    <p className="text-sm text-charcoal-light">Takes approximately 5-7 minutes to answer all questions</p>
                  </div>
                </div>
                
                <div className="flex items-start mb-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center mr-3 mt-1 relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gold to-amber-300"></div>
                    <div className="absolute inset-x-0 top-0 h-[1px] bg-white/30 rounded-t-full"></div>
                    <Layers className="h-5 w-5 text-black relative z-10" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal">Comprehensive</h3>
                    <p className="text-sm text-charcoal-light">Covers 8 key focus areas of PMO maturity with 20 targeted questions</p>
                  </div>
                </div>
                
                <div className="flex items-start mb-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center mr-3 mt-1 relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gold to-amber-300"></div>
                    <div className="absolute inset-x-0 top-0 h-[1px] bg-white/30 rounded-t-full"></div>
                    <LineChart className="h-5 w-5 text-black relative z-10" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal">Actionable Results</h3>
                    <p className="text-sm text-charcoal-light">Receive a detailed maturity score and tailored recommendations</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="font-medium text-charcoal mb-2">What You'll Need</h3>
                <ul className="text-sm text-charcoal-light space-y-2">
                  <li className="flex items-center">
                    <ArrowRight className="h-3 w-3 text-amber-500 mr-2" />
                    <span>Basic understanding of your PMO structure</span>
                  </li>
                  <li className="flex items-center">
                    <ArrowRight className="h-3 w-3 text-amber-500 mr-2" />
                    <span>Knowledge of current PMO practices</span>
                  </li>
                  <li className="flex items-center">
                    <ArrowRight className="h-3 w-3 text-amber-500 mr-2" />
                    <span>Awareness of organizational project challenges</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
        
        <motion.div 
          className="mt-10 text-center text-sm text-charcoal-light"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <p>
            Your information is secure and will only be used to provide you with relevant PMO insights.
            See our <a href="#" className="text-deepBlue hover:text-gold underline">privacy policy</a> for details.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ContactPage;
